import { useNavigate } from 'react-router-dom';
import { cn } from '@/components/ui/utils';
import { useDayTimer, formatElapsed, formatDuration } from '../hooks/useDayTimer';
import type { TimeEntry } from '../types';

interface Props {
  openTimer: TimeEntry | null;
  onTimerChange: (timer: TimeEntry | null) => void;
}

export function DayTimerControl({ openTimer, onTimerChange }: Props) {
  const navigate = useNavigate();
  const { isRunning, elapsedSeconds, startDay, endDay } = useDayTimer(openTimer, onTimerChange);

  if (!isRunning && !openTimer?.ended_at) {
    // Not started
    return (
      <button
        onClick={startDay}
        className={cn(
          'px-4 py-2 rounded-[4px] font-[500] text-[14px] transition-all duration-200',
          'focus-visible:outline-2 focus-visible:outline-offset-2',
          'bg-[var(--color-gold)] text-[var(--color-near-black)]',
          'hover:bg-[var(--color-gold-light)] active:scale-95'
        )}
        aria-label="Start your work day"
      >
        Start day
      </button>
    );
  }

  if (isRunning) {
    // Running
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/intern/time')}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-[4px]',
            'text-[13px] font-[500] tabular-nums',
            'transition-colors duration-200',
            'hover:bg-[rgba(245,240,232,0.1)]'
          )}
          style={{ background: 'var(--color-forest-dark)', color: 'var(--color-warm-white)' }}
          aria-label="View time log"
          aria-live="polite"
        >
          {/* Pulsing gold dot */}
          <span
            className="w-2 h-2 rounded-full shrink-0 animate-pulse"
            style={{ background: 'var(--color-gold)' }}
            aria-hidden
          />
          <span className="tabular-nums">{formatElapsed(elapsedSeconds)}</span>
        </button>

        <button
          onClick={endDay}
          className={cn(
            'px-3 py-1.5 rounded-[4px] text-[13px] font-[500]',
            'border border-[rgba(245,240,232,0.3)] transition-all duration-200',
            'hover:bg-[rgba(245,240,232,0.1)]'
          )}
          style={{ color: 'var(--color-warm-white)' }}
          aria-label="End your work day"
        >
          End
        </button>
      </div>
    );
  }

  // Ended (show restart option)
  const lastDuration = openTimer?.duration_min ?? 0;
  return (
    <button
      onClick={startDay}
      className="text-[13px] font-[400] hover:underline transition-colors"
      style={{ color: 'rgba(245,240,232,0.6)' }}
    >
      Today: {formatDuration(lastDuration)} · Start again
    </button>
  );
}
